import React, { useContext, useEffect, useState } from "react";
import WonmovProvider, { AppContextType, Notification, WonmovContext } from "../context/app-context";
import { User, UserVIPData } from "../entry/server/user";
import { userRepo } from "../repo/user-repo";
import { clearAuthToken, getAuthToken } from "../lib/cookie-manager";
import { WatchListItemRequest } from "../entry/server/movie";
import SuccessAlert from "../component/commom/alerts/success-alert";
import TvNavigation from "./TvNavigation";

const successIcon = "/img/success-alert.png";
const dangerIcon = "/img/danger-alert.png";
const warningIcon = "/img/warning-alert.png";
const infoIcon = "/img/info-alert.png";

type AppShellProps = {
  children: React.ReactNode;
};

export default function AppShell(props: AppShellProps): React.ReactElement {
  const [initialUser, setInitialUser] = useState<User | null>(null);
  const [initialVipData, setInitialVipData] = useState<UserVIPData | null>(null);
  const [initialUnread, setInitialUnread] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(function () {
    userRepo.getMe().then(function (res) {
      if (res.status && res.data) {
        setInitialUser(res.data.user || null);
        setInitialVipData(res.data.vip || null);
        setInitialUnread(res.data.unread_notifications_count || 0);
      } else if (getAuthToken()) {
        clearAuthToken();
      }
      setReady(true);
    }).catch(function () {
      if (getAuthToken()) {
        clearAuthToken();
      }
      setReady(true);
    });

    const playingTime = localStorage.getItem("wonmov-playing-time");
    if (playingTime) {
      userRepo.saveWatchTime(playingTime).then(function (response) {
        if (response.status) {
          localStorage.removeItem("wonmov-playing-time");
        }
      }).catch(function () {});
    }

    const watchTimesString = localStorage.getItem("watch-times");
    if (watchTimesString) {
      try {
        const watchTimes = JSON.parse(watchTimesString) as WatchListItemRequest[];
        userRepo.saveWatchListItem(watchTimes).then(function (response) {
          if (response.status) {
            localStorage.removeItem("watch-times");
          }
        }).catch(function () {});
      } catch (e) {
        localStorage.removeItem("watch-times");
      }
    }
  }, []);

  if (!ready) {
    return (
      <div className="page-loading">
        <div className="loader" />
      </div>
    );
  }

  return (
    <WonmovProvider
      initialUser={initialUser}
      initialVipData={initialVipData}
      initialUnreadNotificationsCount={initialUnread}
    >
      <RootLayout>{props.children}</RootLayout>
    </WonmovProvider>
  );
}

function RootLayout(props: { children: React.ReactNode }): React.ReactElement {
  const { notification, successAlert, setSuccessAlert } = useContext<AppContextType>(WonmovContext);
  return (
    <>
      <TvNavigation />
      {props.children}
      <NotificationView notification={notification} />
      {successAlert && (
        <SuccessAlert
          title={successAlert.title}
          message={successAlert.message}
          onClick={function () {
            setSuccessAlert(null);
          }}
        />
      )}
    </>
  );
}

function NotificationView(props: { notification: Notification | null }): React.ReactElement {
  const { setNotification } = useContext<AppContextType>(WonmovContext);
  const notification = props.notification;

  useEffect(
    function () {
      if (notification && !notification.hidden) {
        const duration = notification.duration !== undefined && notification.duration !== null ? notification.duration : 5000;
        const t1 = window.setTimeout(function () {
          setNotification(Object.assign({}, notification, { hidden: true }));
          window.setTimeout(function () {
            setNotification(null);
          }, 1500);
        }, duration);
        return function () {
          window.clearTimeout(t1);
        };
      }
      return undefined;
    },
    [notification]
  );

  function getNotificationClass(): string {
    if (!notification || notification.hidden) return "translate-y-full";
    if (notification.type === "success") return "notification-success";
    if (notification.type === "danger") return "notification-danger";
    if (notification.type === "warning") return "notification-warning";
    if (notification.type === "info") return "notification-info";
    return "";
  }

  function getNotificationIcon(): string {
    if (!notification) return "#";
    if (notification.type === "success") return successIcon;
    if (notification.type === "danger") return dangerIcon;
    if (notification.type === "warning") return warningIcon;
    if (notification.type === "info") return infoIcon;
    return "#";
  }

  function getNotificationTitle(): string {
    if (!notification) return "";
    if (notification.type === "success") return "موفقیت آمیز!";
    if (notification.type === "danger") return "خطا!";
    if (notification.type === "warning") return "هشدار!";
    if (notification.type === "info") return "اطلاعیه!";
    return "";
  }

  return (
    <div className={"notification-toast " + getNotificationClass()}>
      {notification && <img src={getNotificationIcon()} alt={notification.type} className="size-6" />}
      <div className="notification-body">
        <p className="title">{getNotificationTitle()}</p>
        <p className="message text-sm">{notification && notification.message ? notification.message : ""}</p>
      </div>
    </div>
  );
}
